import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Table from '../Components/Tables';
import Button from '../Components/Buttons';

import { ChannelApiProvider, useChannelApi } from '../Services/ChannelApiProvider';

import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { AlertProvider, useAlert } from '../Components/AlertContext';
import { useConfirmation } from '../Components/ConfirmationContext';
import { formatDateUTC } from '../utils/formatDateUTC';
import { channelTypes } from '../constants/channelTypes';

function ChannelsHub({canEdit}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getChannels, deleteChannel } = useChannelApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [channels, setChannels] = useState([]);
  const [totalChannels, setTotalChannels] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [alertShown, setAlertShown] = useState(false);

  useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state?.returnMessage && !alertShown) {
      showAlert('help', location.state.returnMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: false },
    { name: 'Name', selector: (row) => row.name, sortable: false },
    { name: 'Channel', selector: (row) => channelTypes[row.channel_type], sortable: false },
    { name: 'Status', selector: (row) => row.is_active === true ? "Active" : "InActive", sortable: false },
    { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: false },
    { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: false },
  ];
  
  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/channels/${row.id}`, { state: { organizationId } });
  };

  const handleDelete = async (selectedId) => { 
    if (!selectedId) return;
    showConfirmation(
        `Are you sure you want to delete the selected item?`,
        async () => {
            try {
              setLoading(true);
              await deleteChannel(selectedId); // await deleteDocument
              await fetchChannels(0, itemsPerPage);
              showAlert('success', 'Item was successfully deleted!'); // for now we will show success message
            } catch (error) {
              showAlert('fail', 'Failed to delete the item!');
            } finally {
              setLoading(false);
            }
        }
    );
  };

  const fetchChannels = async (skip, limit) => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }
    setLoading(true);
      try {
        const response = await getChannels(skip, limit);
        setChannels(response.items);
        setTotalChannels(response.totalCount);
        setError(null);
      } catch (err) {
        console.error('Error fetching channels:', err);
        setError('Failed to load channels');
        setChannels([]);
      } finally {
        setLoading(false);
      }
  } 

  useEffect(() => {
    fetchChannels(0, itemsPerPage);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="Channels"
                columns={columns}
                data={channels}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={itemsPerPage}
                paginationTotalRows={totalChannels}
                onRowClicked={handleRowClicked}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={handleDelete}
                onChangePage={fetchChannels}
                getRowUrl={(row) => `/organizations/${organizationId}/channels/${row.id}`}

              />
              {canEdit &&(
                <Button
                  background="color2"
                  size="large"
                  onClick={() => navigate(`/organizations/${organizationId}/channels/new-channel`)}
                >
                  Create New Channel
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ChannelsHubWrapper({canEdit}) {
  return (
    <AlertProvider>
      <ConfirmationProvider>
        <ChannelApiProvider>
          <ChannelsHub canEdit={canEdit}/>
        </ChannelApiProvider>
      </ConfirmationProvider>
    </AlertProvider>
  );
}

export default ChannelsHubWrapper;